import { Mic, MicOff, XIcon } from "lucide-react";

import DefaultProfile from "../../../assets/images/default_profile_new.png";
import { AgentState } from "../realtime-conversation-bar";
import { Card } from "./card";
import { LiveWaveform } from "./live-waveform";
import { cn } from "../../ui/utils";

type VoiceViewProps = {
  allocatedUserName?: string;
  agentState: AgentState;
  isConnected: boolean;
  isMuted: boolean;
  micStream: MediaStream | null;
  className?: string;
  avatar?: string | null;
  onPrimaryClick: () => void;
  onEnd: () => void;
  startDisabled: boolean;
  endDisabled: boolean;
};

const VoiceConversationView = ({
  allocatedUserName,
  agentState,
  isConnected,
  isMuted,
  micStream,
  className,
  avatar,
  onPrimaryClick,
  onEnd,
  startDisabled,
  endDisabled,
}: VoiceViewProps) => {
  const canRenderWave = Boolean(micStream) && isConnected && !isMuted;

  return (
    <Card className={cn("border-0 p-0 shadow-none h-full", className)}>
      <div className="h-full flex flex-col items-center justify-between">
        <div className="mt-2 text-center text-base font-bold text-foreground">
          Hey! this is {allocatedUserName || "Anna"}, how can I help
          you today?
        </div>

        <div className="mt-8 h-40 w-40 overflow-hidden rounded-full bg-muted">
          <img
            src={avatar || DefaultProfile}
            alt="profile"
            className="h-full w-full object-cover"
          />
        </div>

        <div className="mt-8 w-full flex items-center justify-center px-4">
          <LiveWaveform
            stream={micStream}
            active={canRenderWave}
            processing={agentState === "connecting"}
            mode="static"
            height={60}
            barWidth={3}
            barGap={3}
            barRadius={999}
            barColor="#BDBDBD"
            fadeEdges={false}
            sensitivity={1.35}
            smoothingTimeConstant={0.85}
          />
        </div>

        <div className="mt-6 text-sm text-muted-foreground">
          {agentState === "connecting"
            ? "Connecting..."
            : isConnected
              ? isMuted
                ? "Muted"
                : "Listening..."
              : "Tap to start"}
        </div>

        <div className="mt-8 flex items-center justify-center gap-6">
          <button
            type="button"
            onClick={onPrimaryClick}
            disabled={startDisabled}
            className={cn(
              "flex h-12 w-12 items-center justify-center rounded-full shadow-2xl border-0 p-2 cursor-pointer bg-white",
              isConnected && isMuted && "bg-white",
              startDisabled && "opacity-50 cursor-not-allowed",
            )}
            aria-label={
              agentState === "disconnected" ? "Start voice" : "Toggle mute"
            }
          >
            {isConnected && isMuted ? (
              <MicOff className="h-6 w-6" />
            ) : (
              <Mic className="h-6 w-6" />
            )}
          </button>

          <button
            type="button"
            onClick={onEnd}
            disabled={endDisabled}
            className={cn(
              "flex h-12 w-12 items-center justify-center rounded-full text-white p-2 border-0 shadow cursor-pointer bg-red-500",
              endDisabled && "opacity-50 cursor-not-allowed",
            )}
            aria-label="End session"
          >
            <XIcon className="h-6 w-6" />
          </button>
        </div>
      </div>
    </Card>
  );
};

export default VoiceConversationView;
